// Project history list: shows saved projects with topic + timestamp; open/delete.

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ProjectSummary } from "@asmg/shared";
import { api } from "../api/client";
import { Button, ErrorBanner, Spinner } from "../components/ui";

export function HistoryView() {
  const [projects, setProjects] = React.useState<ProjectSummary[] | null>(null);
  const [error, setError] = React.useState<unknown>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const navigate = useNavigate();

  const load = React.useCallback(async () => {
    setError(null);
    try {
      setProjects(await api.listProjects());
    } catch (e) {
      setError(e);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function onDelete(id: string) {
    if (!confirm("Permanently delete this project and its files?")) return;
    setDeletingId(id);
    try {
      await api.deleteProject(id);
      setProjects((prev) => prev?.filter((p) => p.id !== id) ?? null);
    } catch (e) {
      setError(e);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="container">
      <div className="row-between">
        <h2>Your Projects</h2>
        <Link className="btn" to="/new">
          + New Project
        </Link>
      </div>

      <ErrorBanner error={error} onRetry={load} />

      {projects === null && !error && <Spinner label="Loading projects…" />}

      {projects && projects.length === 0 && (
        <div className="card muted">No projects yet. Create one to get started.</div>
      )}

      {projects?.map((p) => (
        <div className="card row-between" key={p.id}>
          <div className="grow" style={{ cursor: "pointer" }} onClick={() => navigate(`/project/${p.id}`)}>
            <div style={{ fontWeight: 600 }}>{p.topic}</div>
            <div className="muted">{new Date(p.createdAt).toLocaleString()}</div>
          </div>
          <div className="row">
            <Link className="btn btn-ghost btn-sm" to={`/project/${p.id}`}>
              Open
            </Link>
            <Button
              variant="danger"
              loading={deletingId === p.id}
              onClick={() => onDelete(p.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
